import React, { useState, useEffect } from 'react';
import { PortfolioData } from '../types/portfolio';

interface NavigationProps {
  data: PortfolioData;
}

const Navigation: React.FC<NavigationProps> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('hero');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Determine which section is in view
      const sections = ['hero', 'about', 'impact', 'experience', 'skills', 'work', 'contact'];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when a link is clicked
  const handleNavClick = () => {
    setIsOpen(false);
  };

  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Impact', href: '#impact' },
    { label: 'Experience', href: '#experience' },
    { label: 'Skills', href: '#skills' },
    { label: 'Work', href: '#work' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="#hero"
            className={`font-manrope font-bold text-xl transition-colors duration-300 ${
              isScrolled ? 'text-navy' : 'text-white'
            }`}
          >
            Divya
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => {
              const sectionId = item.href.replace('#', '');
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={item.label}
                  href={item.href}
                  className={`text-sm font-semibold transition-colors duration-300 pb-2 border-b-2 ${
                    isActive
                      ? isScrolled
                        ? 'border-teal text-teal'
                        : 'border-gold text-gold'
                      : isScrolled
                      ? 'border-transparent text-navy hover:text-teal'
                      : 'border-transparent text-white hover:text-gold'
                  }`}
                >
                  {item.label}
                </a>
              );
            })}
          </div>

          {/* CTA Button */}
          <a
            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${data.personal.email}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden md:inline-block px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
              isScrolled
                ? 'bg-teal text-white hover:bg-opacity-90'
                : 'bg-gold text-navy hover:bg-opacity-90'
            }`}
          >
            Let's Talk
          </a>

          {/* Mobile Menu Button - Three Lines */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 transition-colors duration-300 ${
              isScrolled ? 'text-navy' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <span
              className={`w-6 h-0.5 bg-current transition-all duration-300 block`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-current transition-all duration-300 block`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-current transition-all duration-300 block`}
            ></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden fixed top-16 left-0 right-0 bg-navy shadow-lg animate-slideIn">
            <div className="px-4 py-4 space-y-2 max-h-[calc(100vh-64px)] overflow-y-auto">
              {navItems.map((item) => {
                const sectionId = item.href.replace('#', '');
                const isActive = activeSection === sectionId;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    className={`block px-4 py-3 font-semibold rounded-lg transition-colors ${
                      isActive
                        ? 'bg-gold text-navy'
                        : 'text-white hover:bg-deep-blue hover:text-gold'
                    }`}
                    onClick={handleNavClick}
                  >
                    {item.label}
                  </a>
                );
              })}
              <div className="pt-2 border-t border-white/10 mt-4">
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${data.personal.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-3 bg-gold text-navy font-semibold rounded-lg hover:bg-opacity-90 transition-all text-center"
                  onClick={handleNavClick}
                >
                  Let's Talk
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
